/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_MY_POLICY_DETAIL, GET_MY_PAYOUTS, GET_KYCDOC_POLICYID } from "../graphql";
import { VApplication } from "../../../application";
import { usePolicyId } from "../provider";
import { toHeaderCase } from "js-convert-case";
import config from "../config/featuresSection.config.json";
import moment from "moment";
import jwt_decode from "jwt-decode";
import { AppNavigatorService } from "../../appNavigatorService";

const lookup = require("country-code-lookup");

interface KycDoc {
  fileType: string;
  doc: string;
  name: string;
  poster?: string;
  [key: string]: any;
}

interface KycDocsResponse {
  kycDoc: KycDoc[];
  isEmpty: boolean;
}

interface PolicyResult {
  policyNumber: string;
  pdf?: { pdf: string };
  kycDoc?: KycDoc[];
  status?: string;
  calendarRange?: any;
  description?: string;
  purchasedBy?: any;
  benificiaryDetail?: any;
  useOfProceeds?: any[];
  type?: string;
  blockchainTxnURL?: string;
  isRenewable?: boolean;
  isRenewed?: boolean;
  productDefination?: string;
  coverage?: string;
  premium?: string;
  discountedPremium?: string;
  purchasedAt?: string;
  countryToBeInsured?: string;
  [key: string]: any;
}

interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

const kycDocsStore: { [key: string]: KycDoc[] } = {};

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;

  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }

  private async getKycDocsByPolicyId(policyId: string): Promise<KycDocsResponse> {
    console.log("getKycDocsByPolicyId CALLED with:", policyId);

    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client && policyId) {
        const res = await client.query({
          query: GET_KYCDOC_POLICYID,
          variables: { policyId },
          fetchPolicy: "network-only",
        });

        const docs = res?.data?.getMyKycDocsByPolicyId?.data ?? [];
        kycDocsStore[policyId] = docs;
        localStorage.setItem(`kycDocs_${policyId}`, JSON.stringify(docs));

        console.log("KYC docs stored for policyId", policyId, ":", docs);
        return {
          kycDoc: docs,
          isEmpty: docs.length <= 0,
        };
      }
      return { kycDoc: [], isEmpty: true };
    } catch (err: any) {
      console.log(`Error fetching KYC docs for policyId ${policyId}:`, err);
      return { kycDoc: [], isEmpty: true };
    }
  }

  async getProps(args: any): Promise<any> {
    const { policyNumber } = usePolicyId();
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    try {
      if (client) {
        const policyRes = await client.query({
          query: GET_MY_POLICY_DETAIL,
          variables: {
            input: {
              policyId: policyNumber,
            },
          },
          fetchPolicy: "network-only",
        });

        if (policyRes?.data?.getMyPolicyDetail) {
          console.log(`policy details`, policyRes?.data?.getMyPolicyDetail);
          let _result: PolicyResult = policyRes?.data?.getMyPolicyDetail;

          const kycResult = await this.getKycDocsByPolicyId(policyNumber);
          _result = {
            ..._result,
            kycDoc: kycResult?.kycDoc || [],
          };

          let _tempButtons = await this.getButtons(_result);
          console.log(`tempButton`, _tempButtons);
          return {
            title: _result?.policyNumber,
            subTitle: _result?.policyNumber,
            statusComp: _result?.status
              ? {
                  title: `Current status: ${toHeaderCase(_result?.status)}`,
                  statusType: "success",
                }
              : null,
            calendarRange: _result?.calendarRange,
            listItems: this.getListItems(_result),
            accordionItems: this.getAccordionItems(_result),
            policyDocument: `data:application/pdf;base64,${_result?.pdf?.pdf}`,
            carouselItems: this.getCarouselItems(_result),
            actualPolicyCarouselItems: this.ActualPolicygetCarouselItems(_result),
            useOfProceedDetails: this.getUseOfProceeds(_result),
            buttons: _tempButtons?.buttons,
            acknowledgeData: _tempButtons?.acknowledgeData,
            renewPolicyProp: _result,
          };
        }
      }
      return { ...(config || {}) }; 
    } catch (err: any) {
      console.log(`Error fetching policy or KYC data`, err);
      throw Error(err?.response?.data?.error?.display_message || "Failed to fetch data");
    }
  }

  private ActualPolicygetCarouselItems(result: PolicyResult) {
    let _temp: any[] = [];
    let _kyc: any[] = [];

    if (result?.pdf?.pdf) {
      _temp.push({
        type: "pdf",
        poster: `data:application/pdf;base64,${result?.pdf?.pdf}`,
        src: `data:application/pdf;base64,${result?.pdf?.pdf}`,
        title: "Policy Contract",
      });
    }

    if (Array.isArray(result?.kycDoc)) {
      console.log(`Processing KYC docs in ActualPolicygetCarouselItems`, result?.kycDoc);
      _kyc = result.kycDoc.map((element: KycDoc) => {
        console.log(`element in kycDoc _kyc`, element);
        console.log(`element document link`, element?.doc);
        return {
          ...element,
          type: this.getDocType(element?.fileType),
          src: element?.doc,
          title: element?.name,
          poster: element?.poster,
        };
      });
    }

    return [..._temp, ..._kyc];
  }

  private getCarouselItems(result: PolicyResult) {
    let _temp: any[] = [];
    let _kyc: any[] = [];

    if (result?.pdf?.pdf) {
      _temp.push({
        type: "pdf",
        poster: `data:application/pdf;base64,${result?.pdf?.pdf}`,
        src: `data:application/pdf;base64,${result?.pdf?.pdf}`,
        title: "Policy Contract",
      });
    }
    if (Array.isArray(result?.kycDoc)) {
      _kyc = result.kycDoc.map((element: KycDoc) => {
        return {
          ...element,
          type: this.getDocType(element?.fileType),
          src: element?.doc,
          title: element?.name,
          poster: element?.poster,
        };
      });
    }

    return [..._temp, ..._kyc];
  }


  private async getButtons(data: PolicyResult) {
    const { type, policyNumber } = usePolicyId();
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    let _acknowledgeData: any[] = [];
    let _buttons: any[] = [];

    if (type === "provisional" && data?.status !== "CANCELED" && data?.status !== "EXPIRED") {
      _buttons.push(config?.cancel);
    }
    if (data?.type === "ACTUAL" && data?.blockchainTxnURL) {
      _buttons.push({
        ...config?.viewTransactionButton,
        link: data?.blockchainTxnURL,
      });
    }
    if (type === "actual" || type === "archived") {
      if(type === "actual") {
      _buttons.push(config.analyticsButton);
      }
      if (type === "actual" && data?.isRenewable && !data?.isRenewed) {
        _buttons.push(config.renewPolicyButton);
      }
      try {
        if (client) {
          const res = await client.query({
            query: GET_MY_PAYOUTS,
            variables: {
              input: {
                where: [
                  {
                    column: "policyId",
                    value: [policyNumber],
                    op: "Equal",
                  },
                ],
              },
            },
            fetchPolicy: "network-only",
          });

          if (res?.data?.getMyPayouts) {
            console.log(`policy payouts`, res?.data?.getMyPayouts);
            let _data = res?.data?.getMyPayouts?.data;
            let _tempData =
              Array.isArray(_data) &&
              _data?.filter((ele: any) => !ele?.acknowledeged && ele?.status === "PAID");
            console.log(`filtered Data`, _tempData);
            if (Array.isArray(_tempData) && _tempData?.length > 0) {
              _buttons.push(config?.acknowledge);
            }
            Array.isArray(_tempData) &&
              _tempData?.map((ele: any) => {
                _acknowledgeData.push({
                  label: `On ${moment(ele?.payment?.paidOn).format("LLL")} I received a wire transfer in the amount of ${
                    ele?.payment?.amount
                  } and can confirm that the payout have been received.`,
                  value: ele?.id,
                });
              });
          }
          console.log(`acknowledge payout`, _acknowledgeData);
        }
      } catch (err: any) {
        console.log(`Error fetching payouts`, err?.response?.data);
        throw Error(err?.response?.data?.error?.display_message || "Failed to fetch payouts");
      }
    }
    return {
      buttons: [...(config?.buttons || []), ..._buttons],
      acknowledgeData: [..._acknowledgeData],
    };
  }


  private getDocType(res: string | undefined): string | null {
    switch (res) {
      case "application/pdf":
        return "pdf";
      case "image/png":
      case "image/jpg":
      case "image/jpeg":
        return "img";
      default:
        return null;
    }
  }

  private getAccordionItems(res: PolicyResult) {
    let items: any[] = [];
    let _policyHolder = this.getPolicyDetails(res);
    if (res?.description) {
      items.push({
        type: "text",
        title: "Description",
        data: [res?.description],
      });
    }
    if (_policyHolder) {
      items.push({
        type: "multi-col-list",
        numberOfColumns: "3",
        title: _policyHolder?.title,
        data: [..._policyHolder?.listItems],
      });
    }
    if (res?.purchasedBy) {
      let _holder = this.getPolicyHolder(res?.purchasedBy);
      const { type } = usePolicyId();
      if (_holder) {
        items.push({
          type: "multi-col-list",
          numberOfColumns: "3",
          title: type === "actual" ? "Policy Holder Detail" : type === "provisional" ? "Proposer Detail" : "Policy Holder Detail",
          data: [..._holder?.listItems],
        });
      }
    }
    if (res?.benificiaryDetail) {
      let _temp = this.getBeneficiary(res?.benificiaryDetail);
      items.push({
        type: "multi-col-list",
        numberOfColumns: "3",
        title: _temp?.title,
        data: [..._temp?.listItems],
      });
    }

    let _useOfProceeds = this.getUseOfProceeds(res);
    if (_useOfProceeds) {
      items.push({
        type: "check-list",
        title: _useOfProceeds?.title,
        data: [..._useOfProceeds?.listItems],
      });
    }
    return items;
  }

  private getBeneficiary(res: any) {
    let _beneficiary = {
      firstName: res?.firstName,
      lastName: res?.lastName,
      email: res?.email,
      phoneNumber: res?.mobileNo,
      streetAddress: res?.address?.addressLine1,
      city: res?.address?.city,
      country: res?.address?.country ? lookup.byIso(res?.address?.country)?.country : "-",
    };

    let _temp: any[] = [];
    for (const [key, value] of Object.entries(_beneficiary)) {
      _temp.push({
        title: toHeaderCase(key),
        subTitle: value,
      });
    }
    return {
      title: config?.beneficiaryDetails?.title,
      listItems: _temp,
    };
  }

  private getPolicyHolder(data: any) {
    let _temp = config?.policyHolder?.map((ele: any) => {
      if (ele?.key === "address") {
        return {
          ...ele,
          subTitle: `${data[ele?.key]?.addressLine1}, ${data[ele?.key]?.city}, ${
            data[ele?.key]?.country && data[ele?.key]?.country.length === 2
              ? lookup.byIso(data[ele?.key]?.country)?.country
              : "-"
          }`,
        };
      } else if (ele?.key === "firstName" || ele?.key === "lastName") {
        return {
          ...ele,
          subTitle: toHeaderCase(data[ele?.key] ?? "-"),
        };
      } else {
        return {
          ...ele,
          subTitle: data[ele?.key],
        };
      }
    });
    return {
      listItems: _temp,
    };
  }


  private getPolicyDetails(res: PolicyResult) {
    let _temp = [
      {
        title: "Policy Number",
        subTitle: res?.policyNumber,
      },
      {
        title: "Policy Option",
        subTitle: `Policy Option ${res?.productDefination}`,
      },
      {
        title: "Coverage Amount",
        subTitle: res?.coverage,
      },
      {
        title: "Annual Premium",
        subTitle: res?.premium,
      },
      {
        title: "Purchase on",
        subTitle: moment(res?.purchasedAt).format("DD/MM/YYYY"),
      },
      {
        title: "Policy Type",
        subTitle: toHeaderCase(res?.type ?? "-"),
      },
      {
        title: "Insured Country",
        subTitle: res?.countryToBeInsured ? lookup.byIso(res?.countryToBeInsured)?.country : "-",
      },
    ];
    return {
      title: config?.policyDetails?.title,
      listItems: _temp,
    };
  }

  private getListItems(res: PolicyResult) {
    let _temp = config?.listItems?.map((ele: any) => {
      if (ele?.key === "policyName") {
        return {
          ...ele,
          subTitle: `Policy Option ${res?.productDefination}`,
        };
      }
      if (ele?.key === "coverage") {
        return {
          ...ele,
          subTitle: res?.coverage,
        };
      }
      if (ele?.key === "premium") {
        return {
          ...ele,
          subTitle: res?.premium,
        };
      }
      if (ele?.key === "country") {
        return {
          ...ele,
          subTitle: res?.countryToBeInsured ? lookup.byIso(res?.countryToBeInsured)?.country : "-",
          flag: res?.countryToBeInsured,
        };
      }
      return ele;
    });
    if (res?.discountedPremium && res?.premium !== res?.discountedPremium) {
      _temp.push({
        title: "Discounted Premium",
        subTitle: res?.discountedPremium,
        icon: config?.discountIcon,
      });
    }
    return _temp;
  }

  private getUseOfProceeds(data: PolicyResult) {
    let _temp: any[] = [];
    Array.isArray(data?.useOfProceeds) &&
      data?.useOfProceeds?.forEach((element: any) => {
        if (element.selected) {
          _temp.push(toHeaderCase(element?.value ?? ""));
        }
      });

    return {
      title: config?.useOfProceedsDetail?.title,
      listItems: _temp,
    };
  }

  private getTransactionDetail(items: any) {
    let _temp = Array.isArray(items?.data)
      ? items.data.map((ele: any) => ({
          title: toHeaderCase(ele?.action),
          cardTitle: toHeaderCase(ele?.action),
          cardSubtitle: ele?.payload?.policyNumber,
          cardDetailedText: ele?.when ? moment(parseInt(ele?.when)).format("LLL") : null,
        }))
      : [];

    return {
      title: config?.timelineDetails?.title,
      listItems: _temp,
    };
  }
}